import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck } from "lucide-react";
import { api, errMsg, fmtDate, ESTADOS_CITA } from "@/lib/api";
import { useCentro } from "@/lib/centro";

const TTO = { bono: "Bono TV", sesiones: "Sesiones TV", syntonic: "Syntonic", tomatis: "Tomatis", wivi: "Wivi" };

export const SinVerificar = () => {
  const { centro } = useCentro();
  const [rows, setRows] = useState(null);
  const [todas, setTodas] = useState(false);
  const load = useCallback(() => {
    api.get("/citas/sin-verificar", { params: { centro } }).then((r) => setRows(r.data));
  }, [centro]);
  useEffect(() => {
    load();
  }, [load]);
  const verificar = async (a) => {
    try {
      await api.post(`/appointments/${a.id}/verificar`, { verificada: true });
      toast.success(`Sesión de ${a.patient} verificada`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!rows?.length) return null;
  const hoy = new Date();
  const lun = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - ((hoy.getDay() + 6) % 7) - 7);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const [ini, fin] = [iso(lun), iso(new Date(lun.getFullYear(), lun.getMonth(), lun.getDate() + 6))];
  const semana = rows.filter((a) => a.estado === "atendida" && a.fecha >= ini && a.fecha <= fin).length;
  const todasSemana = async () => {
    if (!window.confirm(`¿Verificar las ${semana} sesiones atendidas de la semana pasada (${fmtDate(ini)} – ${fmtDate(fin)})? Se sumarán a sus tratamientos.`)) return;
    try {
      const { data } = await api.post("/citas/verificar-semana", null, { params: { centro } });
      toast.success(`${data.verificadas} sesiones verificadas`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const vista = todas ? rows : rows.slice(0, 8);
  return (
    <div className="mb-8 bg-white border border-amber-200 rounded-lg p-4 fade-up" data-testid="sin-verificar">
      <div className="flex items-center gap-2 mb-3">
        <ClipboardCheck className="h-5 w-5 text-amber-600" />
        <p className="text-sm font-semibold text-slate-800">Sesiones pasadas sin verificar <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800" data-testid="sin-verificar-count">{rows.length}</span></p>
        <span className="text-[11px] text-slate-500">Al verificar se suman al tratamiento.</span>
        <button type="button" onClick={todasSemana} disabled={!semana} data-testid="verificar-semana"
          className="ml-auto rounded-md bg-emerald-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300">
          Verificar las atendidas de la semana pasada ({semana})
        </button>
      </div>
      <div className="divide-y divide-slate-100">
        {vista.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center gap-3 py-1.5 text-xs" data-testid={`sin-verificar-${a.id}`}>
            <span className="w-24 font-medium text-slate-800">{fmtDate(a.fecha)} {a.inicio}</span>
            <Link to={`/tratamiento/${a.treatment_id}`} className="flex-1 min-w-[160px] truncate text-[#1E3A8A] hover:underline">{a.patient} · {TTO[a.tto_tipo] || a.tto_tipo}</Link>
            <span className="text-slate-500">{a.profesional}</span>
            <span className={`rounded px-1.5 py-0.5 ${a.estado === "atendida" ? "bg-emerald-50 text-emerald-700" : a.estado === "no_vino" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{ESTADOS_CITA[a.estado] || a.estado || "Prevista"}</span>
            <button type="button" onClick={() => verificar(a)} data-testid={`sin-verificar-ok-${a.id}`} className="rounded-md border border-emerald-300 px-2 py-0.5 text-emerald-700 hover:bg-emerald-50">Verificar</button>
          </div>
        ))}
      </div>
      {rows.length > 8 && <button type="button" onClick={() => setTodas(!todas)} className="mt-2 text-[11px] text-[#1E3A8A] underline" data-testid="sin-verificar-todas">{todas ? "Ver menos" : `Ver las ${rows.length}`}</button>}
    </div>
  );
};
